import {
  Select,
  Input,
  InputNumber,
  DatePicker,
  message,
} from "antd";
import TextArea from "antd/lib/input/TextArea";
import { useContext, useEffect, useState } from "react";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";
import { newClientEscalation, buildSortedOptions } from "../../../utils/Constants";
import { useDispatch } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import dayjs from "dayjs";

import {
  fetchUsersThunk,
} from "../../../store/thunks/usersThunk";

import {
  fetchProcessesByClientThunk,
} from "../../../store/thunks/processesThunk";

import {
  fetchClientsByDeptThunk,
} from "../../../store/thunks/clientsThunk";

import {
  fetchDepartmentsSimpleThunk,
} from "../../../store/thunks/departmentsThunk";

import {
  fetchDashboardUsersThunk,
} from "../../../store/thunks/dashboardThunk";

import "./newClientEscalation.css";

const onSearch = () => {};

const EditClientEscalation = ({ editingRecord }) => {
  const dispatch = useDispatch();

  const { globalState, setGlobalState } =
    useContext(GlobalStateContext);

  const [departments, setDepartments] = useState([]);
  const [clients, setClients] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [reportingTLs, setReportingTLs] = useState([]);

  const slsRole = secureLocalStorage.getItem("role");
  const slsLIUId = secureLocalStorage.getItem("lIUId");

  /*
   * --------------------------------------------------
   * 1. LOAD DEPARTMENTS
   * --------------------------------------------------
   */
  const fetchDepartments = async () => {
    try {
      const response = await dispatch(
        fetchDepartmentsSimpleThunk({
          route: "departments",
          action: "findAll",
        }),
      ).unwrap();

      setDepartments(
        response?.map((department) => ({
          label: department?.name,
          value: department?._id,
        })) || [],
      );
    } catch (error) {
      console.error("Error fetching departments:", error);
      setDepartments([]);
    }
  };

  /*
   * --------------------------------------------------
   * 2. LOAD CLIENTS
   * --------------------------------------------------
   */
  const fetchClientsByDepartment = async (departmentId) => {
    if (!departmentId) {
      setClients([]);
      return;
    }

    try {
      const response = await dispatch(
        fetchClientsByDeptThunk({
          department: departmentId,
        }),
      ).unwrap();

      setClients(
        response?.map((client) => ({
          label: client?.name,
          value: client?._id,
        })) || [],
      );
    } catch (error) {
      console.error(
        "Error fetching clients:",
        error,
      );

      setClients([]);
    }
  };

  /*
   * --------------------------------------------------
   * 3. LOAD PROCESSES
   * --------------------------------------------------
   */
  const fetchProcessesByClient = async (
    clientId,
    departmentId,
  ) => {
    if (!clientId) {
      setProcesses([]);
      return;
    }

    try {
      const response = await dispatch(
        fetchProcessesByClientThunk({
          client: clientId,
          department: departmentId,
        }),
      ).unwrap();

      setProcesses(
        response?.map((process) => ({
          label: process?.name,
          value: process?._id,
        })) || [],
      );
    } catch (error) {
      console.error(
        "Error fetching processes:",
        error,
      );

      setProcesses([]);
    }
  };

  /*
   * --------------------------------------------------
   * 4. LOAD REPORTING TL
   * --------------------------------------------------
   */
  const fetchReportingTLByDepartment = async (
    departmentId,
  ) => {
    if (!departmentId) {
      setReportingTLs([]);
      return;
    }

    try {
      const payload = {
        route: "users",
        action: "findAll",
        fields: ["tl", "atl"],
        role: slsRole,
        lIUId: slsLIUId,
        departments: [departmentId],
      };

      const result = await dispatch(
        fetchDashboardUsersThunk(payload),
      ).unwrap();

      const users = Array.isArray(result)
        ? result
        : [];

      const tlUsers = users.filter(
        (user) =>
          user?.role ===
            process.env.REACT_APP_TLROLE ||
          user?.role ===
            process.env.REACT_APP_ATLROLE,
      );

      setReportingTLs(
        buildSortedOptions(
          tlUsers,
          "name",
          "_id",
          true,
          [
            process.env.REACT_APP_TLROLE?.toLowerCase(),
            process.env.REACT_APP_ATLROLE?.toLowerCase(),
          ],
        ),
      );
    } catch (error) {
      console.error(
        "Error fetching Reporting TL:",
        error,
      );

      setReportingTLs([]);
    }
  };

  /*
   * --------------------------------------------------
   * 5. INITIAL LOAD FOR EDIT
   * --------------------------------------------------
   */
  useEffect(() => {
    if (!editingRecord) return;

    fetchDepartments();

    const departmentId =
      editingRecord?.department?._id ||
      editingRecord?.department;

    const clientId =
      editingRecord?.client?._id ||
      editingRecord?.client;

    if (departmentId) {
      fetchClientsByDepartment(departmentId);
      fetchReportingTLByDepartment(departmentId);
    }

    if (departmentId && clientId) {
      fetchProcessesByClient(
        clientId,
        departmentId,
      );
    }
  }, [editingRecord]);

  /*
   * --------------------------------------------------
   * 6. POPULATE EDIT VALUES
   * --------------------------------------------------
   */
  useEffect(() => {
    if (!editingRecord) return;

    const departmentId =
      editingRecord?.department?._id ||
      editingRecord?.department ||
      null;

    const clientId =
      editingRecord?.client?._id ||
      editingRecord?.client ||
      null;

    const processId =
      editingRecord?.process?._id ||
      editingRecord?.process ||
      null;

    const reportingTlId =
      editingRecord?.reportingTl?._id ||
      editingRecord?.reportingTl ||
      null;

    const departmentLabel =
      editingRecord?.department?.name ||
      editingRecord?.departmentName ||
      "";

    const clientLabel =
      editingRecord?.client?.name ||
      editingRecord?.clientName ||
      "";

    const processLabel =
      editingRecord?.process?.name ||
      editingRecord?.processName ||
      "";

    const reportingTlLabel =
      editingRecord?.reportingTl?.name ||
      editingRecord?.reportingTlName ||
      "";

    setGlobalState((prev) => ({
      ...prev,

      eDepartmentName: departmentId,
      eDepartmentNameVal: departmentLabel,

      eClientName: clientId,
      eClientNameVal: clientLabel,

      eProcessName: processId,
      eProcessNameVal: processLabel,

      eReportingTlName: reportingTlId,
      eReportingTlNameVal: reportingTlLabel,

      eClientSpocEmail:
        editingRecord?.clientSpocEmail || null,
      eClientSpocEmailVal:
        editingRecord?.clientSpocEmail || null,

      eEmpID:
        editingRecord?.empID || null,
      eEmpIDVal:
        editingRecord?.empID || null,

      eFinancialImpact:
        editingRecord?.financialImpact || null,
      eFinancialImpactVal:
        editingRecord?.financialImpact || null,

      eAgentName:
        editingRecord?.agent?.name ||
        editingRecord?.agent ||
        null,
      eAgentNameVal:
        editingRecord?.agent?.name ||
        editingRecord?.agent ||
        null,

      eAuditorName:
        editingRecord?.auditor?.name ||
        editingRecord?.auditor ||
        null,
      eAuditorNameVal:
        editingRecord?.auditor?.name ||
        editingRecord?.auditor ||
        null,

      eErrorRaiseDate:
        editingRecord?.errorRaiseDate
          ? dayjs(editingRecord.errorRaiseDate)
          : null,

      eErrorRaiseDateVal:
        editingRecord?.errorRaiseDate
          ? dayjs(editingRecord.errorRaiseDate)
          : null,

      eTransactionId:
        editingRecord?.transactionId || null,
      eTransactionIdVal:
        editingRecord?.transactionId || null,

      eTransactionDate:
        editingRecord?.transactionDate
          ? dayjs(editingRecord.transactionDate)
          : null,

      eTransactionDateVal:
        editingRecord?.transactionDate
          ? dayjs(editingRecord.transactionDate)
          : null,

      eErrorDescription:
        editingRecord?.errorDescription || null,
      eErrorDescriptionVal:
        editingRecord?.errorDescription || null,

      eErrorImpact:
        editingRecord?.errorImpact || null,
      eErrorImpactVal:
        editingRecord?.errorImpact || null,

      eErrorArea:
        editingRecord?.errorArea || null,
      eErrorAreaVal:
        editingRecord?.errorArea || null,

      eErrorType:
        editingRecord?.errorType || null,
      eErrorTypeVal:
        editingRecord?.errorType || null,

      eErrorStatus:
        editingRecord?.errorStatus || null,
      eErrorStatusVal:
        editingRecord?.errorStatus || null,

      eDayoftheweek:
        editingRecord?.dayoftheweek || null,
      eDayoftheweekVal:
        editingRecord?.dayoftheweek || null,

      eAgentTenure:
        editingRecord?.agentTenure || null,
      eAgentTenureVal:
        editingRecord?.agentTenure || null,

      eFeedbackdate:
        editingRecord?.feedbackdate || null,
      eFeedbackdateVal:
        editingRecord?.feedbackdate || null,

      eRepeatedError:
        editingRecord?.repeatedError || null,
      eRepeatedErrorVal:
        editingRecord?.repeatedError || null,

      eCorrectiveActionTaken:
        editingRecord?.correctiveActionTaken || null,
      eCorrectiveActionTakenVal:
        editingRecord?.correctiveActionTaken || null,

      rcaLevelOne:
        editingRecord?.rcaLevelOne || null,
      rcaLevelOneVal:
        editingRecord?.rcaLevelOne || null,

      rcaLevelTwo:
        editingRecord?.rcaLevelTwo || null,
      rcaLevelTwoVal:
        editingRecord?.rcaLevelTwo || null,

      rcaLevelThree:
        editingRecord?.rcaLevelThree || null,
      rcaLevelThreeVal:
        editingRecord?.rcaLevelThree || null,

      rcaLevelFour:
        editingRecord?.rcaLevelFour || null,
      rcaLevelFourVal:
        editingRecord?.rcaLevelFour || null,
    }));
  }, [editingRecord]);

  /*
   * --------------------------------------------------
   * 7. HANDLE SELECT CHANGE
   * --------------------------------------------------
   */
  const handleSelectChange = (
    field,
    value,
    option,
  ) => {
    setGlobalState((prev) => ({
      ...prev,

      [`${field.key}Val`]:
        option?.label || null,

      [field.key]:
        value || field.errorMessage,

      ...(field.key ===
        "eDepartmentName" && {
        eClientName: null,
        eClientNameVal: null,
        eProcessName: null,
        eProcessNameVal: null,
        eReportingTlName: null,
        eReportingTlNameVal: null,
      }),

      ...(field.key ===
        "eClientName" && {
        eProcessName: null,
        eProcessNameVal: null,
      }),
    }));

    if (field.key === "eDepartmentName") {
      fetchClientsByDepartment(value);
      fetchReportingTLByDepartment(value);
    }

    if (field.key === "eClientName") {
      fetchProcessesByClient(
        value,
        globalState?.eDepartmentName,
      );
    }
  };

  return (
    <div id="antdformstyle">
      {newClientEscalation.map((section) => (
        <div key={section.key}>
          <div className="pb-16 cusFormTitle">
            {section.title}
          </div>

          <div className="details-container plr-16">
            {section.fields
              .reduce((rows, field, index) => {
                if (index % 2 === 0) {
                  rows.push(
                    section.fields.slice(
                      index,
                      index + 2,
                    ),
                  );
                }

                return rows;
              }, [])
              .map((fieldPair, pairIndex) => (
                <div
                  className="details-wrap"
                  key={pairIndex}
                >
                  {fieldPair.map((field) => (
                    <div
                      key={field.key}
                      className="details-inner"
                    >
                      <label className="formCusLabel">
                        {field.label}

                        {field.required && (
                          <span className="reqred">
                            *
                          </span>
                        )}
                      </label>

                      {field.type === "input" && (
                        <Input
                          placeholder={
                            field.placeholder
                          }
                          value={
                            globalState?.[
                              `${field.key}Val`
                            ]
                          }
                          onChange={(e) =>
                            setGlobalState((prev) => ({
                              ...prev,

                              [`${field.key}Val`]:
                                e.target.value,

                              [field.key]:
                                e.target.value ||
                                field.errorMessage,
                            }))
                          }
                        />
                      )}

                      {field.type === "number" && (
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder={
                            field.placeholder
                          }
                          value={
                            globalState?.[
                              `${field.key}Val`
                            ]
                          }
                          onChange={(value) => {
                            setGlobalState((prev) => ({
                              ...prev,

                              [`${field.key}Val`]:
                                value,

                              [field.key]:
                                value ||
                                field.errorMessage,
                            }));
                          }}
                        />
                      )}

                      {field.type === "readonly" && (
                        <div className="readonly-field">
                          <span>
                            {
                              globalState?.[
                                `${field.key}Val`
                              ]
                            }
                          </span>
                        </div>
                      )}

                      {field.type === "date" && (
                        <DatePicker
                          style={{ width: "100%" }}
                          placeholder={
                            field.placeholder
                          }
                          value={
                            globalState?.[
                              `${field.key}Val`
                            ]
                          }
                          disabledDate={(current) => {
                            if (
                              field.key ===
                                "eTransactionDate" &&
                              globalState?.eErrorRaiseDateVal
                            ) {
                              return current.isAfter(
                                globalState.eErrorRaiseDateVal,
                                "day",
                              );
                            }

                            return false;
                          }}
                          onChange={(date) => {
                            const dayName = date
                              ? date.format("ddd")
                              : "";

                            setGlobalState(
                              (prev) => ({
                                ...prev,

                                [`${field.key}Val`]:
                                  date,

                                [field.key]:
                                  date ||
                                  field.errorMessage,

                                ...(field.key ===
                                  "eTransactionDate" && {
                                  eDayoftheweekVal:
                                    dayName ||
                                    null,
                                }),
                              }),
                            );
                          }}
                        />
                      )}

                      {field.type === "textarea" && (
                        <TextArea
                          placeholder={
                            field.placeholder
                          }
                          value={
                            globalState?.[
                              `${field.key}Val`
                            ]
                          }
                          onChange={(e) =>
                            setGlobalState((prev) => ({
                              ...prev,

                              [`${field.key}Val`]:
                                e.target.value,

                              [field.key]:
                                e.target.value ||
                                field.errorMessage,
                            }))
                          }
                        />
                      )}

                      {field.type === "select" && (
                        <Select
                          showSearch
                          allowClear
                          placeholder={
                            field.placeholder
                          }
                          optionFilterProp="label"
                          value={
                            globalState?.[
                              `${field.key}Val`
                            ]
                          }
                          onChange={(value, option) =>
                            handleSelectChange(
                              field,
                              value,
                              option,
                            )
                          }
                          filterOption={(
                            input,
                            option,
                          ) =>
                            (
                              option?.label || ""
                            )
                              .toLowerCase()
                              .includes(
                                input.toLowerCase(),
                              )
                          }
                          options={
                            field.key ===
                            "eDepartmentName"
                              ? departments
                              : field.key ===
                                  "eClientName"
                                ? clients
                                : field.key ===
                                    "eProcessName"
                                  ? processes
                                  : field.key ===
                                      "eReportingTlName"
                                    ? reportingTLs
                                    : field.options
                          }
                          disabled={
                            field.key ===
                            "eClientName"
                              ? !globalState?.eDepartmentName
                              : field.key ===
                                  "eProcessName"
                                ? !globalState?.eClientName
                                : field.key ===
                                    "eReportingTlName"
                                  ? !globalState?.eDepartmentName
                                  : false
                          }
                        />
                      )}

                      {globalState?.[
                        `${field.key}`
                      ] === field.errorMessage && (
                        <div className="error">
                          {field.errorMessage}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EditClientEscalation;